import { MenuEntry } from './BuildingMenu';

/**
 * Panoramy nad panelem: jeden plik na parę (rodzaj, szczebel), nazwany jak
 * model tego szczebla — `city-1.png` nad miastem.
 */
const BACKDROPS = '/assets/pictures/backdrops';

/**
 * Jedna rzecz, którą panel ma o tym budynku do powiedzenia.
 *
 * Para, a nie zdanie, bo wiersze mają się do siebie wyrównać: nazwa po lewej,
 * liczba po prawej. Nagłówek (`head`) otwiera grupę takich wierszy.
 */
export interface Fact {
  head?: string;
  label?: string;
  value?: string;
}

/**
 * To, na czym stoisz: panel przy prawej krawędzi ekranu.
 *
 * **Ten sam kształt, co rozmowa, i to jest celowe.** Jedno i drugie jest
 * **stanem, w którym gra się znajduje** — trwa, dopóki się w nim jest, i nie
 * gaśnie przy następnym kliknięciu. Menu pod prawym przyciskiem jest czym
 * innym: odpowiedzią na jeden gest, w miejscu, w którym padł.
 *
 * **Pozycje są kopią menu budynku, a nie drugą listą.** Obie strony pytają
 * `Game.menuFor`, więc pozycja dołożona do menu pojawia się tu sama — a dwie
 * listy rozjechałyby się przy pierwszej takiej okazji. Stąd też `MenuEntry`
 * importowane z `BuildingMenu`: kształt pozycji należy do menu, ten panel tylko
 * go rysuje.
 *
 * **Odświeża się, dopóki stoi**, i tym różni się od menu w praktyce: „Zbierz
 * 12 🪵" rośnie z czasem produkcji, a menu pod kursorem nie miało z tym
 * problemu, bo gasło, zanim liczba zdążyła się zmienić.
 */
export class SpotPanel {
  private readonly root = document.createElement('div');
  /**
   * Panorama miejsca nad nazwą. **Brak pliku chowa ją**, a nie zostawia pustego
   * pasa: grafiki powstają po jednej, a panel bez tła wyglądał dobrze przedtem.
   */
  private readonly backdrop = document.createElement('img');
  private readonly title = document.createElement('div');
  /** Jedno zdanie pod nazwą: dziś ludność osady. */
  private readonly note = document.createElement('div');
  /** Co wiadomo o tym budynku: produkcja, modyfikatory, magazyn. */
  private readonly facts = document.createElement('div');
  private readonly items = document.createElement('div');

  /** Co jest w panelu w tej chwili — żeby nie przemalowywać co sekundę bez potrzeby. */
  private painted = '';

  constructor(
    container: HTMLElement,
    /** „Zamknij” na dole — to samo, co Escape przy tym panelu. */
    private readonly onClose: () => void = () => {}
  ) {
    this.root.className = 'spot';
    this.root.hidden = true;

    this.backdrop.className = 'spot-backdrop';
    this.backdrop.alt = '';
    this.backdrop.draggable = false;
    this.backdrop.hidden = true;
    this.backdrop.addEventListener('load', () => (this.backdrop.hidden = false));
    this.backdrop.addEventListener('error', () => (this.backdrop.hidden = true));

    this.title.className = 'spot-title';
    this.note.className = 'spot-note';
    this.facts.className = 'spot-facts';
    this.items.className = 'spot-items';

    // Prawy przycisk w panelu jest sprawą panelu, nie przeglądarki — tak samo,
    // jak w menu budynku.
    this.root.addEventListener('contextmenu', (event) => event.preventDefault());

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'side-close';
    close.textContent = 'zamknij';
    close.addEventListener('click', () => this.onClose());

    this.root.append(this.backdrop, this.title, this.note, this.facts, this.items, close);
    container.append(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  /**
   * Pokaż to, na czym stoimy — albo schowaj, gdy nie ma czego pokazać.
   *
   * Pusta lista pozycji chowa panel, a nie zostawia pustą ramkę: budynek bez
   * ani jednej rzeczy do zrobienia jest budynkiem, o którym nie ma co mówić.
   *
   * `picture` to nazwa modelu stojącego szczebla — z niej bierze się panorama.
   */
  show(
    title: string,
    entries: MenuEntry[],
    note?: string,
    facts: Fact[] = [],
    picture?: string
  ): void {
    if (entries.length === 0 && facts.length === 0) {
      this.hide();
      return;
    }

    // Odcisk treści, nie liczba pozycji: zmiana samej liczby w „Zbierz 12 🪵"
    // ma przerysować panel, a dołożenie pozycji o tej samej etykiecie — nie
    // przerysować go dwa razy.
    const stamp = `${picture ?? ''}|${title}|${note ?? ''}|${facts
      .map((fact) => `${fact.head ?? ''}/${fact.label ?? ''}/${fact.value ?? ''}`)
      .join('|')}|${entries
      .map((entry) => `${entry.label}/${entry.hint ?? ''}/${entry.enabled}`)
      .join('|')}`;

    if (stamp !== this.painted) {
      this.painted = stamp;
      this.paintBackdrop(picture);
      this.title.textContent = title;
      this.note.textContent = note ?? '';
      this.note.hidden = note === undefined;
      this.facts.replaceChildren(...facts.map((fact) => this.fact(fact)));
      this.facts.hidden = facts.length === 0;
      this.items.replaceChildren(...entries.map((entry) => this.row(entry)));
    }

    this.root.hidden = false;
  }

  /**
   * Podmień panoramę tylko przy zmianie pliku: ten sam adres przypisany drugi
   * raz nie odpala `load`, więc obrazek zostałby schowany na stałe.
   */
  private paintBackdrop(picture: string | undefined): void {
    const src = picture ? `${BACKDROPS}/${picture}.png` : '';
    if (this.backdrop.getAttribute('src') === src) return;

    this.backdrop.hidden = true;
    if (src) this.backdrop.src = src;
    else this.backdrop.removeAttribute('src');
  }

  /** Nagłówek grupy albo wiersz „nazwa — wartość". */
  private fact(fact: Fact): HTMLElement {
    if (fact.head !== undefined) {
      const head = document.createElement('h4');
      head.textContent = fact.head;
      return head;
    }

    const line = document.createElement('div');
    line.className = 'spot-fact';
    const label = document.createElement('span');
    label.textContent = fact.label ?? '';
    const value = document.createElement('span');
    value.textContent = fact.value ?? '';
    line.append(label, value);
    return line;
  }

  private row(entry: MenuEntry): HTMLElement {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'spot-item';
    item.disabled = !entry.enabled;

    const label = document.createElement('span');
    label.className = 'spot-label';
    label.textContent = entry.label;
    item.append(label);

    if (entry.hint) {
      const hint = document.createElement('span');
      hint.className = 'spot-hint';
      hint.textContent = entry.hint;
      item.append(hint);
    }

    if (entry.enabled && entry.choose) {
      const chosen = entry.choose;
      // Panel **zostaje otwarty**, w odróżnieniu od menu, które po wyborze
      // gaśnie. Menu było odpowiedzią na kliknięcie i po akcji nie ma po co
      // stać; ten panel opisuje pole, na którym dalej się stoi — a zgaszony po
      // każdym zbiorze musiałby się zaraz otworzyć z powrotem.
      item.addEventListener('click', () => chosen());
    }

    return item;
  }

  hide(): void {
    this.root.hidden = true;
    // Wyczyszczony odcisk, żeby ponowne wejście przerysowało panel od zera:
    // przez czas, gdy był schowany, wszystko mogło się zmienić.
    this.painted = '';
  }

  dispose(): void {
    this.root.remove();
  }
}
